import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, UncontrolledTooltip } from 'reactstrap'
import { AxiosError } from 'axios'
import { apiClient } from '../../../api/client'
import { HistoryState } from '../../../api/globalTypes'
import notifications from '../../../api/notifications/hooks'
import {
    NotificationActivity,
    NotificationAuthorization,
    NotificationDeliveryBase,
    NotificationDocument,
    NotificationMatchedOffer,
    NotificationWebsiteSync,
} from '../../../api/notifications/types'
import { formatDateTime } from '../../../utils/general'
import { showDismissUndoToast } from '../utils'

interface NotificationTableRow extends NotificationDeliveryBase {
    disabled?: boolean
}

const handleModule = (parentModule: string, contactType: string) => {
    switch (parentModule) {
        case 'project':
            return 'projects'
        case 'property':
            return 'properties'
        case 'contact':
            return contactType === 'person' ? 'contacts' : 'companies'
        default:
            return ''
    }
}

const handleLink = (activeTab: string, row: NotificationTableRow): string | { pathname: string; state: HistoryState } => {
    switch (activeTab) {
        case 'authorization':
            return `/properties/${(row as NotificationAuthorization).property.id}`
        case 'documents': {
            const { parentModule, parentId, contactType } = (row as NotificationDocument).document
            const appModule = handleModule(parentModule, contactType)
            return `/${appModule}/${parentId}`
        }
        case 'activities': {
            const { contactId } = (row as NotificationActivity).activity
            return contactId ? `/contacts/${contactId}` : '/activities'
        }
        case 'offers': {
            const { contactId, contactType, profileId } = (row as NotificationMatchedOffer).contact
            const appModule = handleModule('contact', contactType)
            return {
                pathname: `/${appModule}/${contactId}`,
                state: { activeTab: 'demand', profileId: String(profileId) },
            }
        }
        case 'other':
            return `/properties/${(row as NotificationWebsiteSync).other.propertyId}`
        default:
            return '#'
    }
}

const getEndpointUrl = (activeTab: string, row: NotificationTableRow): string => {
    switch (activeTab) {
        case 'authorization':
            return `v1/property/${(row as NotificationAuthorization).property.id}`
        case 'documents': {
            const { parentModule, parentId } = (row as NotificationDocument).document
            switch (parentModule) {
                case 'property':
                    return `v1/property/${parentId}`
                case 'contact':
                    return `v1/contact/${parentId}`
                case 'project':
                    return `v1/project/${parentId}`
                default:
                    return ''
            }
        }
        case 'activities': {
            const { contactId } = (row as NotificationActivity).activity
            return contactId ? `v1/contact/${contactId}` : ''
        }
        case 'offers':
            return `v1/contact/${(row as NotificationMatchedOffer).contact.contactId}`
        case 'other':
            return `v1/property/${(row as NotificationWebsiteSync).other.propertyId}`
        default:
            return ''
    }
}

const useTabColumns = (activeTab: string): ColumnDef<NotificationTableRow>[] => {
    const { t } = useTranslation()

    const tabColumns: Record<string, ColumnDef<NotificationTableRow>[]> = useMemo(
        () => ({
            authorization: [
                {
                    header: t('app.common.id'),
                    id: 'id',
                    accessorKey: 'property.internalAppId',
                    enableSorting: false,
                    meta: { thDivClass: 'text-center' },
                },
                {
                    header: t('app.common.type'),
                    id: 'propertyType',
                    enableSorting: false,
                    accessorFn: (row) => (row as NotificationAuthorization).property?.propertyType,
                },
                {
                    header: t('app.common.service'),
                    id: 'propertyService',
                    enableSorting: false,
                    accessorFn: (row) => (row as NotificationAuthorization).property?.propertyService,
                },
                {
                    header: t('app.common.status'),
                    id: 'propertyStatus',
                    enableSorting: false,
                    accessorFn: (row) => (row as NotificationAuthorization).property?.propertyStatus,
                },
            ],
            documents: [
                {
                    header: t('table.header.documents.parentEntity'),
                    id: 'parentEntity',
                    enableSorting: false,
                    cell: ({ row }) => {
                        const { parentName, parentLocation, parentInternalAppId } = (row.original as NotificationDocument).document ?? {}
                        const text = `${parentName}${parentLocation ? `, ${parentLocation}` : ''} (ID: ${parentInternalAppId})`
                        const tooltipTarget = `parent-entity-${row.original.deliveryId}`

                        return (
                            <div id={tooltipTarget} className="text-truncate" style={{ maxWidth: 200 }}>
                                {text}
                                <UncontrolledTooltip placement="top" target={tooltipTarget}>
                                    {text}
                                </UncontrolledTooltip>
                            </div>
                        )
                    },
                },
                {
                    header: t('app.common.documentType'),
                    id: 'documentType',
                    enableSorting: false,
                    cell: ({ row }) => {
                        const { documentTypeName, fileInternalAppId } = (row.original as NotificationDocument).document ?? {}

                        return <span>{`${documentTypeName} (ID: ${fileInternalAppId})`}</span>
                    },
                },
                {
                    header: t('app.common.expirationDate'),
                    id: 'documentValidityDate',
                    enableSorting: false,
                    size: 80,
                    meta: { tdClass: 'text-center' },
                    cell: ({ row }) => formatDateTime((row.original as NotificationDocument).document?.documentValidityDate),
                },
            ],
            activities: [
                {
                    header: t('app.common.dateFrom'),
                    id: 'dateFrom',
                    enableSorting: false,
                    size: 120,
                    cell: ({ row }) => {
                        const { dateFrom, timeFrom } = (row.original as NotificationActivity).activity ?? {}

                        if (!dateFrom) return null

                        const value = timeFrom ? `${dateFrom}T${timeFrom}` : dateFrom

                        return <span className="text-nowrap">{formatDateTime(value, !!timeFrom)}</span>
                    },
                },
                {
                    header: t('app.common.name'),
                    id: 'title',
                    enableSorting: false,
                    size: 150,
                    cell: ({ row }) => {
                        const title = (row.original as NotificationActivity).activity?.title ?? ''

                        return (
                            <div className="text-truncate" style={{ maxWidth: 200 }} title={title}>
                                {title}
                            </div>
                        )
                    },
                },
                {
                    header: t('app.common.activityType'),
                    id: 'activityType',
                    enableSorting: false,
                    cell: ({ row }) => {
                        const { activityTypeName } = (row.original as NotificationActivity).activity ?? {}

                        return <span>{activityTypeName ?? ''}</span>
                    },
                },
                {
                    header: t('app.common.reminderTime'),
                    id: 'leadTime',
                    enableSorting: false,
                    size: 160,
                    meta: { thDivClass: 'text-center' },
                    accessorFn: (row) => (row as NotificationActivity).activity?.leadTime,
                },
            ],
            offers: [
                {
                    header: t('app.common.contact.nameAndSurname'),
                    id: 'contactName',
                    enableSorting: false,
                    size: 150,
                    cell: ({ row }) => {
                        const name = (row.original as NotificationMatchedOffer).contact?.contactName ?? ''
                        return <span>{name}</span>
                    },
                },
                {
                    header: t('app.common.property.type'),
                    id: 'propertyType',
                    enableSorting: false,
                    cell: ({ row }) => {
                        const type = (row.original as NotificationMatchedOffer).contact?.propertyType ?? ''
                        return <span>{type}</span>
                    },
                },
                {
                    header: t('table.header.serviceWithPropertyCount'),
                    id: 'profileService',
                    enableSorting: false,
                    cell: ({ row }) => {
                        const { profileService, matchedPropertyCount } = (row.original as NotificationMatchedOffer).contact ?? {}
                        const label = profileService ? `${profileService} (${matchedPropertyCount})` : ''

                        return <span>{label}</span>
                    },
                },
            ],
            other: [
                {
                    header: t('app.common.date'),
                    id: 'date',
                    enableSorting: false,
                    size: 100,
                    cell: ({ row }) => <span className="text-nowrap">{formatDateTime(row.original?.createdAt, true)}</span>,
                },
                {
                    header: t('app.common.message'),
                    id: 'message',
                    enableSorting: false,
                    size: 300,
                    accessorKey: 'message',
                },
            ],
        }),
        [t]
    )

    return useMemo(() => tabColumns[activeTab] ?? [], [tabColumns, activeTab])
}

export const useTableColumns = (activeTab: string): ColumnDef<NotificationTableRow>[] => {
    const { t } = useTranslation()
    const history = useHistory()
    const tabColumns = useTabColumns(activeTab)

    const { mutate: toggleMarkAsRead } = notifications.ToggleMarkAsRead.useMutation()
    const { mutate: dismissNotification } = notifications.DismissNotifications.useMutation()
    const { mutate: restoreNotification } = notifications.RestoreNotifications.useMutation()

    const navigateToEntity = useCallback(
        async (row: NotificationTableRow) => {
            const endpointUrl = getEndpointUrl(activeTab, row)

            if (endpointUrl) {
                try {
                    await apiClient(endpointUrl)
                } catch (error) {
                    if ((error as AxiosError).response?.status === 404) {
                        toast.error(t('notification.linkedEntityDeleted') as string)
                        return
                    }
                    throw error
                }
            }
            history.push(handleLink(activeTab, row))
        },
        [t, history, activeTab]
    )

    const actionsColumn: ColumnDef<NotificationTableRow> = useMemo(
        () => ({
            id: 'actions',
            enableSorting: false,
            enableResizing: false,
            size: 80,
            meta: { thDivClass: 'text-center', stopPropagation: true },
            cell: ({ row }) => {
                const isUnread = row.original.status === 'unread'

                return (
                    <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                        <li className="list-inline-item">
                            <Button
                                color="link"
                                title={isUnread ? t('notification.markAsRead') : t('notification.read')}
                                className={`d-inline-block p-0 ${isUnread ? 'text-success' : 'text-muted'}`}
                                onClick={() => toggleMarkAsRead({ deliveryIds: [row.original.deliveryId] })}
                            >
                                <i className={`mdi ${isUnread ? 'mdi-email-outline' : 'mdi-email-open-outline'} fs-16`}></i>
                            </Button>
                        </li>
                        <li className="list-inline-item">
                            <Button
                                color="link"
                                title={t('button.view')}
                                className="text-warning d-inline-block p-0"
                                onClick={async () => {
                                    if (row.original.status === 'unread') toggleMarkAsRead({ deliveryIds: [row.original.deliveryId] })
                                    await navigateToEntity(row.original)
                                }}
                            >
                                <i className="mdi mdi-eye-outline fs-16"></i>
                            </Button>
                        </li>
                        <li className="list-inline-item edit">
                            <Button
                                color="link"
                                title={t('button.delete')}
                                className="text-danger d-inline-block p-0"
                                onClick={() =>
                                    dismissNotification(
                                        { deliveryIds: [row.original.deliveryId] },
                                        {
                                            onSuccess: (data) => {
                                                const deliveryIds = data?.data?.updatedIds
                                                if (deliveryIds?.length) showDismissUndoToast(deliveryIds, restoreNotification)
                                            },
                                        }
                                    )
                                }
                            >
                                <i className="mdi mdi-trash-can-outline fs-16"></i>
                            </Button>
                        </li>
                    </ul>
                )
            },
        }),
        [t, toggleMarkAsRead, navigateToEntity, dismissNotification, restoreNotification]
    )

    return useMemo(() => [...tabColumns, actionsColumn], [tabColumns, actionsColumn])
}
